export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'userData',
  WALLET_ADDRESS: 'connectedWalletAddress',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const DEFAULT_TTL_MS = 5000;
const cache = new Map<string, CacheEntry<unknown>>();

export function getItem<T = string>(key: StorageKey, ttl = DEFAULT_TTL_MS): T | null {
  const cached = cache.get(key);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.value as T;
  }

  if (typeof window === 'undefined') return null;

  try {
    const value = localStorage.getItem(key);
    if (value === null) return null;

    let parsed: T;
    try {
      parsed = JSON.parse(value) as T;
    } catch {
      parsed = value as unknown as T;
    }

    cache.set(key, { value: parsed, expiresAt: Date.now() + ttl });
    return parsed;
  } catch (error) {
    console.error(`Error reading ${key} from storage:`, error);
    return null;
  }
}

export function setItem<T>(key: StorageKey, value: T, ttl = DEFAULT_TTL_MS): void {
  if (typeof window === 'undefined') return;

  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    cache.set(key, { value, expiresAt: Date.now() + ttl });
  } catch (error) {
    console.error(`Error writing ${key} to storage:`, error);
  }
}

export function removeItem(key: StorageKey): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
    cache.delete(key);
  } catch (error) {
    console.error(`Error removing ${key} from storage:`, error);
  }
}

export function clearAuthStorage(): void {
  removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  removeItem(STORAGE_KEYS.USER_DATA);
}

export function invalidateCache(key: StorageKey): void {
  cache.delete(key);
}

export function clearCache(): void {
  cache.clear();
}

export function getRawItem(key: StorageKey): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error reading ${key} from storage:`, error);
    return null;
  }
}
